import { parseJsonConf, parseStorageJsonConf } from "OK/utils/parseJsonConf";
import * as vanilla from "OK/utils/vanilla";
import { debugLog } from "./utils";
import type { IHash } from "OK/utils/utils";

enum ActivityType {
    FEED = 'FEED',
    GAMES = 'GAMES',
    VIDEOS = 'VIDEOS',
    HOBBY = 'HOBBY',
    MESSAGES = 'MESSAGES',
    OTHER = 'OTHER',
    PLAYER = 'PLAYER'
}
type ActivityMap<K extends string | number | symbol = string, V = string> = Record<K, V[]>;

interface SavedData {
    activityType?: ActivityType;
    timestamp?: number;
    playerTimestamp?: number;
}

enum SessionEvent {
    START = '3',
    CONTINUATION = '4',
    END = '5'
}

const DEFAULT_PING = 30_000;
const DEFAULT_PING_MARGIN = 500;
const DEFAULT_LAYER_RESET_TIME = 100;
const DEFAULT_STORAGE_KEY = 'ms.state';
const DEFAULT_TARGET_HOST = '.ms.ok.ru';

const storage: Storage = window.localStorage;

export default class MediascopeTracker {
    // Интервал отправки битов
    pingInterval!: number;
    // Ключ в LocalStorage
    storageKey!: string;
    // Дефолтный тип активности (OTHER)
    defaultActivityType!: ActivityType;
    isUserAuthorized: boolean = false;
    // Хост медиаскопа
    targetHost!: string;
    // Get параметры для отправки на хост медиаскопа
    searchParamsToSend: IHash<unknown> | null = null;

    // Включено ли логирование плеера
    withPlayerLogging: boolean = false;
    // Является ли плеер embedded (влияет на урл)
    isEmbeddedPlayer: boolean = false;
    // Запущен ли плеер в леере (добавляет технические особенности инициализации логирования плеера)
    isPlayerViaLayer: boolean = false;
    // Логировался ли конец сессии плеера
    wasPlayerEndSent: boolean = false;
    // Ожидается ли отправка поддержания сессии плеера
    shouldSendPlayerHeartbeat: boolean = false;
    // Последний таймстемп плеера
    lastPlayerLogTimestamp: number | null = null;
    // Таймер айди плеера
    playerTimerId: ReturnType<typeof setTimeout> | null = null;

    // Включено ли логирование страницы
    withPageLogging: boolean = false;
    // Текущий StateId. Нужен для определения ActivityType
    currentStateId!: string;
    // Текущий LayerId. Нужен для определения ActivityType
    currentLayerId: string | null = null;
    // Текущий LayerId. Нужен для определения факта изменения ActivityType
    prevLayerId: string | null = null;
    // Текущий ActivityType
    currentActivityType!: ActivityType;
    // Предыдущий ActivityType. Нужен для определения необходимости отправки конца сессии страницы
    prevActivityType!: ActivityType;

    // Мапа ActivityType (Enum) к численному значению. Влияет на формирование урла, куда отправляется heartbeat
    typeToId!: ActivityMap<ActivityType, number>;
    // Мапа StateId к ActivityType
    stateIdToType!: ActivityMap<string, ActivityType>;
    // Мапа LayerId к ActivityType
    layerIdToType!: ActivityMap<string, ActivityType>;

    timerId?: ReturnType<typeof setTimeout>;
    // Флаг, был ли леер только что переоткрыт. Особенность лееров том, что глобальный колбек всегда сначала регистрирует закрытие,
    // даже если другой леер остается открытым
    layerWasJustReset: boolean = false;
    // Интервал, больший чем время между событиями закрытия и открытия леера, если хотя бы 1 леер остается открытым
    layerResetTime?: number;

    // Рубильник на включку форматтера времени для старых браузеров, см. OKFRONT-684
    isOldBrowserFormatterEnabled: boolean = false;

    public activate(el: HTMLElement): void {
        const { targetHost, storageKey, pingInterval, isUserAuthorized, searchParamsToSend, typeToId, isOldBrowserFormatterEnabled } = parseJsonConf(el, 'data-core-config');

        const pageLoggingConfig = parseJsonConf(el, 'data-page-logging-config');
        const playerLoggingConfig = parseJsonConf(el, 'data-player-logging-config');

        this.withPageLogging = pageLoggingConfig.withPageLogging !== undefined ? pageLoggingConfig.withPageLogging : true;
        this.withPlayerLogging = playerLoggingConfig.withPlayerLogging !== undefined ? playerLoggingConfig.withPlayerLogging : true;

        this.targetHost = targetHost || DEFAULT_TARGET_HOST;
        this.storageKey = storageKey || DEFAULT_STORAGE_KEY;
        this.pingInterval = pingInterval ? Number(pingInterval) : DEFAULT_PING;
        this.isUserAuthorized = isUserAuthorized;
        this.searchParamsToSend = searchParamsToSend;
        this.typeToId = typeToId;
        this.isOldBrowserFormatterEnabled = isOldBrowserFormatterEnabled;

        let savedData: SavedData | null | undefined;

        if (this.withPageLogging) {
            const { currentStateId, defaultActivityType, layerResetTime, typeToStateId, typeToLayerId } = pageLoggingConfig;

            // Логика модуля предполагает, что он будет вставлен в самый верх главного блока с контентом,
            // который каждый раз при навигации будет его реактивировать и прокидывать актуальный стейт айди.
            this.currentStateId = currentStateId;
            this.layerResetTime = layerResetTime ? Number(layerResetTime) : DEFAULT_LAYER_RESET_TIME;
            this.defaultActivityType = <ActivityType>defaultActivityType;

            this.stateIdToType = this.revertMap(typeToStateId);
            this.layerIdToType = this.revertMap(typeToLayerId);

            savedData = this.restoreData();
            // Отложенно инитим из-за лееров
            OK.loader.use('OKCustomJs', () => {
                this.initForPage(savedData?.activityType, savedData?.timestamp);
            });
        }
        if (this.withPlayerLogging) {
            this.isEmbeddedPlayer = playerLoggingConfig.isEmbeddedPlayer;

            savedData = savedData || this.restoreData();
            this.initForPlayer(savedData?.playerTimestamp);
        }
    }

    public deactivate(): void {
        if (this.withPageLogging) {
            clearInterval(this.timerId);
            OK.Layers.unsubscribeGlobal(this.onLayerToggle);
        }

        if (this.withPlayerLogging) {
            clearTimeout(this.playerTimerId!);
            this.playerTimerId = null;
            document.removeEventListener('__videoPlayerEvent', this.onVideoPlayerEvent);
            document.removeEventListener('adv', this.onVideoPlayerEvent);
        }
    }

    private initForPage = (activityType?: ActivityType, timestamp?: number): void => {
        this.currentLayerId = OK.Layers.getTopLayerId();
        this.refreshActivityTypes();

        OK.Layers.subscribeGlobal(this.onLayerToggle);

        this.debugLog(`Init finished! Current stateId: ${this.currentStateId}`);

        this.processState(activityType, timestamp);
    };

    private initForPlayer = (lastTimestamp?: number): void => {
        this.lastPlayerLogTimestamp = lastTimestamp!;
        this.shouldSendPlayerHeartbeat = true;

        document.addEventListener('__videoPlayerEvent', this.onVideoPlayerEvent);
        // Событие старта рекламы в плеере
        document.addEventListener('adv', this.onVideoPlayerEvent);
    };

    private onLayerToggle = (): void => {
        const prevType = this.currentActivityType;
        const intermediateLayerId = OK.Layers.getTopLayerId();

        this.layerWasJustReset = false;

        // При переключении лееров сначала прилетает событие с новым layer'ом равным null,
        // поэтому чтоб это не ломало логику - кешируем значение и сравниваем его через момент времени.
        // Используем макротаски. Должно корректно работать 3 кейса: открытие леера, переключение и закрытие.

        if (intermediateLayerId === null) {
            // Сюда заходим при закрытии и в 1 итерацию переключения
            setTimeout(() => {
                if (!this.layerWasJustReset) {
                    // Сюда заходим при переключении. В 3 итерацию
                    this.prevLayerId = this.currentLayerId;
                    this.currentLayerId = intermediateLayerId;
                }
                this.performLayerChange(prevType);
            }, this.layerResetTime);
        } else {
            // Сюда заходим при открытии и в 2 итерацию переключения
            this.prevLayerId = this.currentLayerId;
            this.currentLayerId = intermediateLayerId;
            this.layerWasJustReset = true;
            setTimeout(() => {
                if (this.layerWasJustReset) {
                    this.performLayerChange(prevType);
                }
            }, this.layerResetTime);
        }
    };

    private onVideoPlayerEvent = (): void => {
        const timestamp = Date.now();
        this.debugLog('player event! Session will be started or extended.');

        if (this.lastPlayerLogTimestamp && !isNaN(this.lastPlayerLogTimestamp)) {
            const lastPingTimeDiff = timestamp - this.lastPlayerLogTimestamp;
            // DEFAULT_PING_MARGIN используем из-за гонки событий в эвент лупе
            if ((lastPingTimeDiff - DEFAULT_PING_MARGIN >= this.pingInterval) || this.wasPlayerEndSent) {
                this.lastPlayerLogTimestamp = timestamp;
                this.wasPlayerEndSent = false;
                this.sendHeartbeats(timestamp, true, true);
            } else {
                this.shouldSendPlayerHeartbeat = true;
            }
        } else {
            this.lastPlayerLogTimestamp = timestamp;
            this.sendHeartbeats(timestamp, true, true);
        }

        if (!this.playerTimerId) {
            const timeDiff = timestamp - this.lastPlayerLogTimestamp;
            let pingDelay = this.pingInterval - timeDiff;
            // Дефолтное время, если:
            // 1) значение из кеша некорректное;
            // 2) последний эвент был давно (>this.pingInterval);
            // 3) только что залогировали в коде выше;
            if (isNaN(timeDiff) || timeDiff > this.pingInterval || timeDiff === 0) {
                pingDelay = this.pingInterval;
            }

            this.playerTimerId = setTimeout(() => {
                this.debugLog(`Video timeout expired. Will be send: ${this.shouldSendPlayerHeartbeat}`);
                if (this.shouldSendPlayerHeartbeat) {
                    this.lastPlayerLogTimestamp = Date.now();
                    this.sendHeartbeats(this.lastPlayerLogTimestamp, false, true);
                }
                this.playerTimerId = null;
            }, pingDelay);
        }
    };

    private performLayerChange(prevType: ActivityType): void {
        const wasPlayedViaLayer = this.isPlayerViaLayer;

        this.refreshActivityTypes();
        this.debugLog(`layerId change! Current layerId: ${this.currentLayerId}. CurrentType: ${this.currentActivityType}, prevType: ${prevType}`);

        if (this.currentActivityType !== prevType) {
            this.processState(prevType);
        }

        if (this.withPlayerLogging && wasPlayedViaLayer) {
            this.performPlayerEnding();
        }
    }

    private performPlayerEnding(): void {
        // Завершение сессии плеера приходится логировать вот так сбоку.
        // Считаем только кейс, когда закрываем содержащий видео леер.
        if (this.prevLayerId && !this.currentLayerId) {
            // Обнуляем по таймеру, потому что не хотим запускать поддержку сессии сразу после закрытия леера
            // из-за срабатывания нескольких НЕАКТУАЛЬНЫХ видео эвентов с подгрузкой
            setTimeout(() => {
                clearTimeout(this.playerTimerId!);
                this.playerTimerId = null;
                this.wasPlayerEndSent = true;
            }, DEFAULT_LAYER_RESET_TIME);

            const sessionEvent = SessionEvent.END;
            const activityType = ActivityType.PLAYER;
            const url = this.calcUrl(sessionEvent, activityType);
            const formattedTimestamp = this.formatDate(Date.now());

            this.sendHeartbeat(url, activityType, sessionEvent, formattedTimestamp);
        }
    }

    private processState(prevType?: ActivityType | null, lastPingTimestamp?: number | null): void {
        const activityTypeHasChanged = prevType !== this.currentActivityType;
        const currentTimestamp = Date.now();

        if (activityTypeHasChanged) {
            this.prevActivityType = prevType!;
            // Логирование новой сессии при смене типа активности
            this.sendHeartbeats(currentTimestamp, true);
            this.resetTimer();
            return;
        }
        let additionalDebugData;

        if (lastPingTimestamp && !isNaN(lastPingTimestamp)) {
            const timeDiff = currentTimestamp - lastPingTimestamp;
            additionalDebugData = `Time diff: ${this.formatDate(timeDiff)}. Prev time: ${this.formatDate(lastPingTimestamp)}.`;

            if (timeDiff >= this.pingInterval) {
                // Логирование новой сессии из-за прохождения времени больше допустимого интервала.
                // Предположительно свежая сессия после окончания предыдущей
                this.sendHeartbeats(currentTimestamp, true, false, additionalDebugData);
                this.resetTimer();
            } else if (!this.timerId) {
                // Если заинитили страницу, а после предыдущего бита еще не прошел this.pingInterval
                this.resetTimer(this.pingInterval - timeDiff);
            }
        } else {
            // Логирование новой сессии из-за отсутствия timestamp'a предыдущей сессии
            // Предположительно первая сессия или сессия после очистки кук или в инкогнито
            this.sendHeartbeats(currentTimestamp, true);
            this.resetTimer();
        }
    }

    private refreshActivityTypes(): void {
        this.prevActivityType = this.currentActivityType;
        this.isPlayerViaLayer = false;

        if (this.currentLayerId) {
            // Приоритизируем layerId над stateId для определения типа активности
            const layerTypes = this.layerIdToType[this.currentLayerId];
            if (layerTypes?.length) {
                // Делаем пометку, что плеер сейчас проигрывается в леере. Под леером он должен выключаться
                this.isPlayerViaLayer = true;
                this.currentActivityType = layerTypes[0];
                return;
            }
            this.debugLog(`current layerId "${this.currentLayerId}" is not binded to type!`);
            this.currentActivityType = this.defaultActivityType;
            return;
        }

        if (this.stateIdToType[this.currentStateId]?.length) {
            this.currentActivityType = this.stateIdToType[this.currentStateId][0];
            return;
        }
        this.debugLog(`current stateId "${this.currentStateId}" is not binded to type. So we fallback to it as to "other" type.`);
        this.currentActivityType = this.defaultActivityType;
    }

    private sendHeartbeatsTimerWrapper = (): void => {
        this.timerId = setTimeout(() => this.sendHeartbeatsTimerWrapper(), this.pingInterval);
        this.sendHeartbeats();
    };

    private sendHeartbeats = (timestamp: number = Date.now(), shouldStartNewSession = false, isPlayer = false, additionalDebugData = ''): void => {
        const formattedTimestamp = this.formatDate(timestamp);

        if (shouldStartNewSession && this.prevActivityType && !isPlayer) {
            // Сначала пишем конец пред. сессии
            const sessionEvent = SessionEvent.END;
            const activityType = this.prevActivityType;
            const url = this.calcUrl(sessionEvent, activityType);

            this.sendHeartbeat(url, activityType, sessionEvent, formattedTimestamp, additionalDebugData);
        }
        const sessionEvent = shouldStartNewSession ? SessionEvent.START : SessionEvent.CONTINUATION;
        const activityType = isPlayer ? ActivityType.PLAYER : this.currentActivityType;
        const url = this.calcUrl(sessionEvent, activityType);

        if (isPlayer) {
            this.saveVideoData(timestamp);
        } else {
            this.saveData(activityType, timestamp);
        }

        this.sendHeartbeat(url, activityType, sessionEvent, formattedTimestamp, additionalDebugData);
    };

    private sendHeartbeat(url: string, activityType: string, sessionEvent: SessionEvent, formattedTimestamp: string, additionalDebugData = ''): void {
        this.debugLog(`
            Sending signal!
            Activity type: ${activityType}.
            Session event: ${sessionEvent}.
            Timestamp: ${formattedTimestamp}.
            Url: ${url}.
            ${additionalDebugData}`
        );

        vanilla.ajax<void>({
            url,
            type: 'GET',
            isExternal: true,
            responseType: 'text',
        });
    }

    private calcUrl(sessionEvent: SessionEvent, activityType: ActivityType): string {
        // См. доку медиаскопа. Пока хардкод
        // https://wiki.odkl.ru/pages/viewpage.action?pageId=172520882&preview=/172520882/181376206/%D0%A1%D0%BF%D0%B5%D1%86%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%86%D0%B8%D1%8F%20%D0%BF%D0%BE%20%D1%81%D0%BF%D0%B5%D1%86%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D1%83%20OK.docx
        return 'https://'
            + [
                this.typeToId[activityType],
                sessionEvent,
                this.isEmbeddedPlayer ? 3 : 2,
                2,
                this.isUserAuthorized ? 2 : 3,
                1
            ].join('')
            + this.targetHost
            + (
                this.searchParamsToSend
                    ? `?${Object.keys(this.searchParamsToSend)
                        .map((paramKey) => `${paramKey}=${this.searchParamsToSend?.[paramKey]}`)
                        .join('&')}`
                    : ''
            );
    }

    private revertMap<K extends string = string, V extends string = string>(activityMap: ActivityMap | null): ActivityMap<K, V> {
        if (!activityMap) {
            return <ActivityMap<K, V>>{};
        }
        const map = <ActivityMap<K, V>>{};

        for (let type in activityMap) {
            activityMap[type].forEach((layerId) => {
                if (map[layerId as K]) {
                    map[layerId as K].push(type as V);
                } else {
                    map[layerId as K] = [type as V];
                }
            });
        }
        return map;
    }

    private saveData(activityType: ActivityType, timestamp: number): void {
        try {
            storage.setItem(this.storageKey, JSON.stringify(<SavedData>{
                activityType,
                timestamp,
                playerTimestamp: this.lastPlayerLogTimestamp
            }));
        } catch (e) {
            this.debugLog('Error while saving data to storage!');
        }
    }

    private saveVideoData(timestamp: number): void {
        const restoredData = this.restoreData() || <SavedData>{};
        if (restoredData) {
            restoredData.playerTimestamp = timestamp;
        }
        storage.setItem(this.storageKey, JSON.stringify(restoredData));
    }

    private restoreData(): SavedData | null {
        try {
            return parseStorageJsonConf(storage, this.storageKey);
        } catch (e) {
            this.debugLog('Error while restoring data from storage!');
            return null;
        }
    }

    private resetTimer(interval = this.pingInterval): void {
        clearTimeout(this.timerId);
        // В таймере логирование поддержания сессии
        this.timerId = setTimeout(this.sendHeartbeatsTimerWrapper, interval);
    }

    private debugLog(...msgArgs: unknown[]): void {
        debugLog('MediascopeTracker', ...msgArgs);
    }

    private formatDate(timestamp: number): string {
        if (this.isOldBrowserFormatterEnabled) {
            const date: Date = new Date(timestamp);
            if (isNaN(date.getTime())) {
                this.debugLog(`Invalid timestamp ${timestamp}`);
            }

            const padWithZero = (num: number): string => {
                return num < 10 ? '0' + num : num.toString();
            }

            // Преобразуем время к RU формату
            const hours: string = padWithZero(date.getHours());
            const minutes: string = padWithZero(date.getMinutes());
            const seconds: string = padWithZero(date.getSeconds());

            return `${hours}:${minutes}:${seconds}`;
        }

        return new Date(timestamp).toLocaleTimeString('ru');
    }
}
